import { useState, useEffect } from 'react';
import { Bell, Loader2, Check } from 'lucide-react';
import { useSectionTranslation } from '@/hooks';
import { api, isApiError } from '@/lib/api';
import { settingsTranslations } from './translations';

interface NotificationPrefs {
  emailChat: boolean;
  emailOrders: boolean;
  emailSecurity: boolean;
  emailMarketing: boolean;
  siteNotifications: boolean;
}

function ToggleRow({
  label,
  desc,
  checked,
  onToggle,
}: {
  label: string;
  desc: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-surface-card border border-surface-border px-5 py-4">
      <div>
        <p className="text-sm font-medium text-brand-white">{label}</p>
        <p className="text-xs text-brand-muted">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === ' ') { e.preventDefault(); onToggle(); } }}
        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
          checked ? 'bg-accent' : 'bg-surface-border'
        }`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
            checked ? 'left-[22px]' : 'left-0.5'
          }`}
        />
      </button>
    </div>
  );
}

export function NotificationSettings() {
  const t = useSectionTranslation(settingsTranslations);
  const [emailChat, setEmailChat] = useState(true);
  const [emailOrders, setEmailOrders] = useState(true);
  const [emailSecurity, setEmailSecurity] = useState(true);
  const [emailMarketing, setEmailMarketing] = useState(false);
  const [siteNotifications, setSiteNotifications] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    api.get<Partial<NotificationPrefs>>('/auth/notification-settings').then((res) => {
      if (isApiError(res)) return;
      if (typeof res.data.emailChat === 'boolean') setEmailChat(res.data.emailChat);
      if (typeof res.data.emailOrders === 'boolean') setEmailOrders(res.data.emailOrders);
      if (typeof res.data.emailSecurity === 'boolean') setEmailSecurity(res.data.emailSecurity);
      if (typeof res.data.emailMarketing === 'boolean') setEmailMarketing(res.data.emailMarketing);
      if (typeof res.data.siteNotifications === 'boolean') setSiteNotifications(res.data.siteNotifications);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    const res = await api.patch('/auth/notification-settings', {
      emailChat,
      emailOrders,
      emailSecurity,
      emailMarketing,
      siteNotifications,
    });
    setSaving(false);
    if (isApiError(res)) {
      setSaveError(res.error?.message || t.notifications.saveError);
      return;
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h2 className="text-lg font-semibold text-brand-white mb-1">{t.notifications.title}</h2>
        <p className="text-sm text-brand-muted">{t.notifications.desc}</p>
      </div>

      {saveError && (
        <div className="flex items-center gap-2 rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-3 py-2.5 text-sm text-brand-danger" role="alert">
          {saveError}
        </div>
      )}

      <div className="space-y-3">
        <ToggleRow
          label={t.notifications.emailChat.label}
          desc={t.notifications.emailChat.desc}
          checked={emailChat}
          onToggle={() => setEmailChat((v) => !v)}
        />
        <ToggleRow
          label={t.notifications.emailOrders.label}
          desc={t.notifications.emailOrders.desc}
          checked={emailOrders}
          onToggle={() => setEmailOrders((v) => !v)}
        />
        <ToggleRow
          label={t.notifications.emailSecurity.label}
          desc={t.notifications.emailSecurity.desc}
          checked={emailSecurity}
          onToggle={() => setEmailSecurity((v) => !v)}
        />
        <ToggleRow
          label={t.notifications.emailMarketing.label}
          desc={t.notifications.emailMarketing.desc}
          checked={emailMarketing}
          onToggle={() => setEmailMarketing((v) => !v)}
        />
        <ToggleRow
          label={t.notifications.siteNotifications.label}
          desc={t.notifications.siteNotifications.desc}
          checked={siteNotifications}
          onToggle={() => setSiteNotifications((v) => !v)}
        />
        {!siteNotifications && (
          <p className="text-xs text-brand-muted px-1">{t.notifications.siteMuted}</p>
        )}
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="btn-primary h-10 disabled:opacity-60"
      >
        {saving ? (
          <><Loader2 size={15} className="animate-spin" />{t.notifications.saving}</>
        ) : saved ? (
          <><Check size={15} />{t.notifications.saved}</>
        ) : (
          <><Bell size={15} />{t.notifications.save}</>
        )}
      </button>
    </div>
  );
}
